import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Fade, InputAdornment,
  MenuItem, Paper, Snackbar, Stack, TextField, Typography
} from "@mui/material";
import { FileDownload as DownloadIcon, Refresh as RefreshIcon, Search as SearchIcon } from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import * as XLSX from "xlsx";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from "../../components/AppHeader";
import { fetchEducationQualificationsReport } from "../../services/webReportsService";

const CATEGORIES = ["", "ACADEMIC", "DIPLOMA", "CERTIFICATION", "TRAINING", "LICENSE"];
const QUALIFICATION_STATUSES = ["", "COMPLETED", "PURSUING", "INCOMPLETE"];
const EMPLOYMENT_STATUSES = ["", "PROBATION", "PERMANENT", "CONTRACT", "RESIGNED", "TERMINATED"];

const Metric = ({ label, value, accent = "#334155" }) => (
  <Paper variant="outlined" sx={{ px: 2, py: 1.5, borderRadius: 3, minWidth: 180, flex: 1 }}>
    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, textTransform: "uppercase" }}>{label}</Typography>
    <Typography variant="h6" sx={{ color: accent, fontWeight: 900, mt: 0.25 }}>{value}</Typography>
  </Paper>
);

export default function EducationQualificationsReport({ setIsLoggedIn }) {
  const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
  const [category, setCategory] = useState("");
  const [qualificationStatus, setQualificationStatus] = useState("");
  const [employmentStatus, setEmploymentStatus] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(employeeSearch.trim()), 350);
    return () => clearTimeout(timer);
  }, [employeeSearch]);

  const showMessage = useCallback((severity, message) => setSnackbar({ open: true, severity, message }), []);

  const loadReport = useCallback(async () => {
    if (!tenant) return;
    setLoading(true);
    try {
      const result = await fetchEducationQualificationsReport({
        category,
        qualification_status: qualificationStatus,
        employment_status: employmentStatus,
        employee_search: debouncedSearch || undefined,
      });
      setRows(Array.isArray(result?.rows) ? result.rows : []);
      setMetrics(result?.metrics || {});
    } catch (error) {
      setRows([]);
      setMetrics({});
      showMessage("error", error?.message || "Failed to load education report.");
    } finally {
      setLoading(false);
    }
  }, [category, debouncedSearch, employmentStatus, qualificationStatus, showMessage, tenant]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const exportExcel = () => {
    const exportRows = rows.map(row => ({
      "Employee ID": row.employee_id || row.staff_code,
      "Staff Name": row.full_name,
      "Employment Status": row.employment_status,
      "Department": row.department,
      "Branch": row.branch_name,
      "Category": row.category,
      "Level": row.level,
      "Field of Study": row.field_of_study,
      "Major Subject": row.major_subject,
      "Institution": row.institution_name,
      "Start Date": row.start_date,
      "End Date": row.end_date,
      "Qualification Status": row.qualification_status,
      "Grade / Result": row.grade_result,
      "CGPA": row.cgpa,
      "Credential ID": row.credential_id,
      "Overseas": row.is_overseas ? "Yes" : "No",
      "Country": row.country,
      "Expiry Date": row.expiry_date,
      "Attachment": row.attachment_name,
      "Remarks": row.remarks,
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Education Qualifications");
    XLSX.writeFile(workbook, "Education_Qualifications_Report.xlsx");
  };

  const columnDefs = useMemo(() => [
    { headerName: "Employee ID", field: "employee_id", width: 145, valueGetter: ({ data }) => data?.employee_id || data?.staff_code || "-" },
    { headerName: "Staff Name", field: "full_name", minWidth: 180, flex: 1 },
    { headerName: "Employment", field: "employment_status", width: 135 },
    { headerName: "Department", field: "department", minWidth: 160 },
    { headerName: "Branch", field: "branch_name", minWidth: 150 },
    { headerName: "Category", field: "category", width: 140, cellRenderer: ({ value }) => <Chip size="small" label={value || "-"} variant="outlined" color="primary" sx={{ fontWeight: 700 }} /> },
    { headerName: "Level", field: "level", minWidth: 170 },
    { headerName: "Field of Study", field: "field_of_study", minWidth: 180 },
    { headerName: "Major Subject", field: "major_subject", minWidth: 180 },
    { headerName: "Institution", field: "institution_name", minWidth: 220, flex: 1 },
    { headerName: "Status", field: "qualification_status", width: 135 },
    { headerName: "End Date", field: "end_date", width: 125 },
    { headerName: "Grade / Result", field: "grade_result", minWidth: 150 },
    { headerName: "CGPA", field: "cgpa", width: 95 },
    { headerName: "Country", field: "country", minWidth: 130 },
    { headerName: "Credential ID", field: "credential_id", minWidth: 155 },
    { headerName: "Expiry Date", field: "expiry_date", width: 125 },
  ], []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <AppHeader setIsLoggedIn={setIsLoggedIn} />
      <Fade in timeout={400}>
        <Box sx={{ p: { xs: 2, md: 3 }, display: "flex", flexDirection: "column", gap: 2.25 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900, color: "#0f172a" }}>Education & Qualifications Report</Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>Detailed employee education, certification, training, diploma, and licence records.</Typography>
            </Box>
            <Stack direction="row" gap={1} alignItems="center">
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadReport} disabled={loading}>Refresh</Button>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportExcel} disabled={loading || rows.length === 0}>Export Excel</Button>
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
            <Metric label="Qualifications" value={Number(metrics.total_qualifications || 0).toLocaleString()} />
            <Metric label="Employees" value={Number(metrics.qualified_employees || 0).toLocaleString()} accent="#7c3aed" />
            <Metric label="Completed" value={Number(metrics.completed || 0).toLocaleString()} accent="#15803d" />
            <Metric label="Pursuing" value={Number(metrics.pursuing || 0).toLocaleString()} accent="#0369a1" />
          </Stack>

          <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "none" }}>
            <Stack direction={{ xs: "column", lg: "row" }} gap={1.5}>
              <TextField select label="Category" value={category} onChange={e => setCategory(e.target.value)} size="small" sx={{ minWidth: 170 }}>
                {CATEGORIES.map(value => <MenuItem key={value || "all"} value={value}>{value || "All categories"}</MenuItem>)}
              </TextField>
              <TextField select label="Qualification Status" value={qualificationStatus} onChange={e => setQualificationStatus(e.target.value)} size="small" sx={{ minWidth: 195 }}>
                {QUALIFICATION_STATUSES.map(value => <MenuItem key={value || "all"} value={value}>{value || "All statuses"}</MenuItem>)}
              </TextField>
              <TextField select label="Employment Status" value={employmentStatus} onChange={e => setEmploymentStatus(e.target.value)} size="small" sx={{ minWidth: 185 }}>
                {EMPLOYMENT_STATUSES.map(value => <MenuItem key={value || "all"} value={value}>{value || "All employees"}</MenuItem>)}
              </TextField>
              <TextField label="Employee ID / Name" value={employeeSearch} onChange={e => setEmployeeSearch(e.target.value)} size="small" sx={{ flex: 1, minWidth: 260 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "none" }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.25 }}>
              <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 800 }}>{loading ? "Loading..." : `${rows.length.toLocaleString()} qualification${rows.length === 1 ? "" : "s"}`}</Typography>
              {loading && <CircularProgress size={20} />}
            </Stack>
            <Box className="ag-theme-quartz" sx={{ height: "calc(100vh - 395px)", minHeight: 470, width: "100%", "& .ag-header-cell-label": { fontWeight: 800 } }}>
              <AgGridReact rowData={rows} columnDefs={columnDefs} defaultColDef={{ sortable: true, filter: true, resizable: true }} rowHeight={48} headerHeight={48} pagination paginationPageSize={25} overlayNoRowsTemplate="<span style='color:#64748b'>No education or qualification records found</span>" />
            </Box>
          </Paper>
        </Box>
      </Fade>
      <Snackbar open={snackbar.open} autoHideDuration={4500} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
